// ===========================================
// BRAINHUB CORE: Sidebar Dinamico
// ===========================================

import { useMemo, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { Sparkles } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useTheme } from '@/lib/theme'
import { isCopilotoEnabled } from '@/lib/featureFlags'
import { usePermissions } from '@/hooks/usePermissions'

import { useWorkspace } from '../providers/WorkspaceProvider'
import { useOrganizacion } from '../providers/OrganizacionProvider'
import { useSidebarState } from '../hooks/useSidebarState'
import { MentaLogo } from './MentaLogo'
import { WorkspaceSwitcher } from './WorkspaceSwitcher'
import {
  SidebarNavItem,
  SidebarSection,
  SidebarUserFooter,
  SidebarTooltip,
  SidebarTooltipProvider,
} from './sidebar'
import { CORE_NAVIGATION, CLIENTES_NAV, ADMIN_NAVIGATION } from '../constants/navigation'
import type { NavigationItem } from '../types/workspace'

// Item de navegación del copiloto (ORR-5) — solo visible para users con el feature flag
const COPILOTO_NAV_ITEM: NavigationItem = {
  id: 'copiloto',
  name: 'Copiloto',
  href: '/copiloto',
  icon: Sparkles,
}

// ===========================================
// COMPONENTE PRINCIPAL
// ===========================================

export function DynamicSidebar() {
  const { workspaceActual, getNavigation, workspacesDisponibles } = useWorkspace()
  const { organizacion, membresia } = useOrganizacion()
  const { puede } = usePermissions()
  const { user: authUser } = useAuth0()
  const copilotoEnabled = isCopilotoEnabled(authUser)
  const { resolvedTheme } = useTheme()
  const [logoError, setLogoError] = useState(false)

  const {
    state,
    setMode,
    setHovered,
    SIDEBAR_WIDTH_COLLAPSED,
    SIDEBAR_WIDTH_EXPANDED,
  } = useSidebarState()

  const { isExpanded, mode } = state

  // Determinar URL del logo segun el tema actual (para sidebar expandido)
  const logoUrl = useMemo(() => {
    const branding = organizacion?.branding
    if (!branding) return null

    const { logo_light_url, logo_dark_url } = branding

    // Si hay error de carga, no mostrar logo
    if (logoError) return null

    // Seleccionar logo segun tema con fallback
    if (resolvedTheme === 'dark') {
      return logo_dark_url || logo_light_url || null
    }
    return logo_light_url || logo_dark_url || null
  }, [organizacion?.branding, resolvedTheme, logoError])

  // Determinar URL del icono (para sidebar colapsado)
  const iconUrl = useMemo(() => {
    const branding = organizacion?.branding
    if (!branding) return null

    // Si hay error de carga, no mostrar
    if (logoError) return null

    const { icon_light_url, icon_dark_url } = branding

    // Seleccionar icono segun tema con fallback
    if (resolvedTheme === 'dark') {
      // En modo oscuro: preferir icono oscuro, fallback a claro, fallback a logo
      return icon_dark_url || icon_light_url || logoUrl
    }
    // En modo claro: preferir icono claro, fallback a oscuro, fallback a logo
    return icon_light_url || icon_dark_url || logoUrl
  }, [organizacion?.branding, resolvedTheme, logoError, logoUrl])

  // Navegacion del workspace actual.
  // Filtro seguro de permisos: solo se oculta un item si su permiso está
  // explícitamente en false. Permisos no mapeados (undefined) se muestran,
  // para no romper items con claves legacy (Casillero/Calendario/Impuestos).
  const workspaceNavigation = useMemo(() => {
    return getNavigation().filter(
      (item) => !item.permission || puede[item.permission as keyof typeof puede] !== false
    )
  }, [getNavigation, puede])

  // Items core/admin filtrados por permiso efectivo (ORR-192): mismo criterio seguro que el workspace.
  const clientesNav = useMemo(
    () =>
      CLIENTES_NAV.filter(
        (item) => !item.permission || puede[item.permission as keyof typeof puede] !== false
      ),
    [puede]
  )
  const adminNav = useMemo(
    () =>
      ADMIN_NAVIGATION.filter(
        (item) => !item.permission || puede[item.permission as keyof typeof puede] !== false
      ),
    [puede]
  )

  // Agrupar navegacion por grupo (de los navigationGroups del workspace)
  const groupedNavigation = useMemo(() => {
    const groups: Record<string, NavigationItem[]> = {}

    workspaceActual.navigationGroups.forEach((group) => {
      groups[group.id] = []
    })

    workspaceNavigation.forEach((item) => {
      const groupId = item.group || 'default'
      if (!groups[groupId]) {
        groups[groupId] = []
      }
      groups[groupId].push(item)
    })

    return groups
  }, [workspaceNavigation, workspaceActual.navigationGroups])

  return (
    <SidebarTooltipProvider delayDuration={0}>
      <aside
        className={cn(
          // `h-full`, no `h-screen`: el contenedor ya descuenta el banner de entorno (ORR-378).
          'hidden md:flex h-full flex-col border-r bg-card transition-all duration-300 ease-in-out',
          mode === 'hover' && isExpanded && 'shadow-lg'
        )}
        style={{
          width: isExpanded ? SIDEBAR_WIDTH_EXPANDED : SIDEBAR_WIDTH_COLLAPSED,
        }}
        onMouseEnter={() => mode === 'hover' && setHovered(true)}
        onMouseLeave={() => mode === 'hover' && setHovered(false)}
      >
        {/* Logo */}
        <div
          className={cn(
            'flex h-14 items-center border-b px-3',
            !isExpanded && 'justify-center'
          )}
        >
          <MentaLogo
            workspace={workspaceActual.id}
            variant={isExpanded ? 'full' : 'icon'}
          />
        </div>

        {/* Workspace Switcher (si hay mas de uno) */}
        {workspacesDisponibles.length > 1 && (
          <WorkspaceSwitcher
            variant="compact"
            className="px-2 py-2 border-b"
            collapsed={!isExpanded}
          />
        )}

        {/* Organizacion / Logo */}
        {organizacion && (
          <div className={cn(
            'border-b bg-muted/30 transition-all duration-200',
            isExpanded ? 'px-3 py-3' : 'px-2 py-2 flex justify-center'
          )}>
            {isExpanded ? (
              // Sidebar expandido
              logoUrl ? (
                // Mostrar logo grande si existe
                <SidebarTooltip content={organizacion.nombre} side="right">
                  <div className="flex items-center justify-center w-full">
                    <img
                      src={logoUrl}
                      alt={organizacion.nombre}
                      className="h-12 max-w-full object-contain"
                      onError={() => setLogoError(true)}
                    />
                  </div>
                </SidebarTooltip>
              ) : (
                // Sin logo: mostrar nombre
                <>
                  <p className="text-[10px] text-muted-foreground/70 uppercase tracking-wider">
                    Organizacion
                  </p>
                  <p className="text-sm font-medium truncate">{organizacion.nombre}</p>
                </>
              )
            ) : (
              // Sidebar colapsado
              iconUrl ? (
                // Mostrar icono si existe
                <SidebarTooltip content={organizacion.nombre} side="right">
                  <div className="flex items-center justify-center">
                    <img
                      src={iconUrl}
                      alt={organizacion.nombre}
                      className="h-8 w-8 object-contain"
                      onError={() => setLogoError(true)}
                    />
                  </div>
                </SidebarTooltip>
              ) : (
                // Sin icono: mostrar inicial
                <SidebarTooltip content={organizacion.nombre} side="right">
                  <div className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center cursor-default">
                    <span className="text-xs font-semibold text-primary">
                      {organizacion.nombre.charAt(0).toUpperCase()}
                    </span>
                  </div>
                </SidebarTooltip>
              )
            )}
          </div>
        )}

        {/* Navigation */}
        <ScrollArea className="flex-1 px-2 py-3">
          <nav className="space-y-1">
            {/* Copiloto (feature flag ORR-5, se muestra arriba del todo) */}
            {copilotoEnabled && (
              <SidebarNavItem
                item={COPILOTO_NAV_ITEM}
                isExpanded={isExpanded}
              />
            )}

            {/* Core Navigation (Inicio suelto, ORR-192) */}
            {CORE_NAVIGATION.map((item) => (
              <SidebarNavItem key={item.id} item={item} isExpanded={isExpanded} />
            ))}

            {/* Gestión de clientes (ORR-192) */}
            <SidebarSection
              label="Clientes"
              items={clientesNav}
              isExpanded={isExpanded}
            />

            {/* Workspace Navigation por grupos */}
            {workspaceActual.navigationGroups.map((group) => {
              const items = groupedNavigation[group.id] || []
              return (
                <SidebarSection
                  key={group.id}
                  label={group.label}
                  items={items}
                  isExpanded={isExpanded}
                />
              )
            })}

            {/* Admin Navigation — visible si el usuario tiene algún permiso de la sección (ORR-192) */}
            {adminNav.length > 0 && (
              <SidebarSection
                label="Administracion"
                items={adminNav}
                isExpanded={isExpanded}
              />
            )}
          </nav>
        </ScrollArea>

        {/* User Footer */}
        <SidebarUserFooter
          isExpanded={isExpanded}
          mode={mode}
          onModeChange={setMode}
          rol={membresia?.rol}
        />
      </aside>
    </SidebarTooltipProvider>
  )
}
